import React, { useState } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { usePractice } from './contexts/PracticeContext';
import PracticeList from './components/PracticeList';
import PracticeView from './components/PracticeView';
import PracticeForm from './components/PracticeForm';
import ResultsView from './components/ResultsView';
import SharedListView from './components/SharedListView';
import { trackEvent } from './utils/analytics';

/**
 * Main content component that handles the app's main views
 */
const MainContent: React.FC = () => {
  const { 
    practices, 
    currentPractice, 
    setCurrentPractice, 
    showForm, 
    setShowForm,
    showResults,
    setShowResults,
    finalResults,
    setFinalResults,
    setNewPracticeName,
    setNewPracticeWords,
    setEditingPractice,
  } = usePractice();
  
  const [showPracticeView, setShowPracticeView] = useState(false);
  
  const handleStartPractice = (practice: any) => {
    setCurrentPractice(practice);
    setShowPracticeView(true);
    
    // Track practice start event
    trackEvent('practice_started', { 
      practice_name: practice.name,
      word_count: practice.words.length 
    });
  };
  
  const handleBackToList = () => {
    setShowPracticeView(false);
    setCurrentPractice(null);
  };
  
  const handleShowResults = (results: { correct: number; incorrect: number }) => {
    setFinalResults(results);
    setShowResults(true);
    setShowPracticeView(false);
  };
  
  const handleBackToResults = () => {
    setShowResults(true);
    setShowPracticeView(false);
  };
  
  const handleFinishResults = () => {
    setShowResults(false);
    setCurrentPractice(null);
  };
  
  const handleAddNew = () => {
    setEditingPractice(null);
    setNewPracticeName('');
    setNewPracticeWords('');
    setShowForm(true);
  };
  
  const handleEditPractice = (practice: any) => {
    setEditingPractice(practice);
    setShowForm(true);
  };
  
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingPractice(null);
  };
  
  const renderContent = () => {
    if (showForm) {
      return <PracticeForm onCancel={handleCancelForm} />;
    }
    
    if (showResults) {
      return (
        <ResultsView 
          results={finalResults}
          practice={currentPractice}
          onFinish={handleFinishResults}
          onPracticeAgain={() => {
            // Reset the practice session before showing the practice view again
            setShowResults(false);
            setShowPracticeView(true);
          }}
        />
      );
    }
    
    if (showPracticeView && currentPractice) {
      return (
        <PracticeView 
          practice={currentPractice}
          onBack={handleBackToList}
          onShowResults={handleShowResults}
          onBackToResults={handleBackToResults}
        />
      );
    }
    
    return (
      <PracticeList 
        practices={practices}
        onStartPractice={handleStartPractice}
        onAddNew={handleAddNew}
        onEditPractice={handleEditPractice}
      />
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-100 p-2 sm:p-3">
      <main className="max-w-4xl mx-auto">
        {renderContent()}
      </main>
      <footer className="mt-4 text-center text-gray-500 text-sm">
        <p> {new Date().getFullYear()} Happy Spell App</p>
      </footer>
    </div>
  );
};

/**
 * Main App component that serves as the container for all other components
 * @returns JSX.Element
 */
const App: React.FC = () => {
  return (
    <Router>
      <Switch>
        <Route path="/shared/:shareId" component={SharedListView} />
        <Route path="/" component={MainContent} />
      </Switch>
    </Router>
  );
};

export default App;
